import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { Button, Input } from '@/components/ui';
import type { IntegrationSettingItem } from '@/types';

/**
 * Prikkerne i skjult tilstand. Fast antal og ikke vaerdiens laengde: laengden paa en noegle
 * er i sig selv en oplysning, og den skal foerst kunne ses naar man aabner oejet.
 */
const MASK = '••••••••••••';

/**
 * Én raekke i integrationskonfigurationen: navn, vaerdi og et øje til højre.
 *
 * Øjet er lukket som udgangspunkt, og det er ikke bare en maske: vaerdien er slet ikke hentet
 * endnu - listen baerer den ikke - saa det foerste klik henter den. Derfor to tilstande, ikke
 * én: om feltet *vises* (`visible`), og om vi *har* vaerdien (`draft !== null`).
 */
export function IntegrationSettingRow({
  setting,
  readOnly = false,
  canReveal = !readOnly,
}: {
  setting: IntegrationSettingItem;
  /** Uden 'Ændr integrationer': no typing, Gem or Slet. */
  readOnly?: boolean;
  /**
   * 'Se integrationsnøgler': the eye that shows the stored value. Without it, only the name and
   * whether the key is set - the API refuses the value too.
   */
  canReveal?: boolean;
}) {
  const queryClient = useQueryClient();

  /** Hvad der staar i feltet. null indtil oejet har hentet vaerdien. */
  const [draft, setDraft] = useState<string | null>(setting.value);
  /**
   * Den vaerdi feltet sidst var enig med serveren om. Kan ikke udledes af `setting.value`,
   * som altid er null - saa ville "Gem" blive staaende aktiv efter gemning, som om det
   * ikke var lykkedes.
   */
  const [baseline, setBaseline] = useState<string | null>(setting.value);
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const reveal = useMutation({
    mutationFn: () =>
      api.get<{ name: string; value: string }>(
        `/integrations/config/${encodeURIComponent(setting.name)}/reveal`,
      ),
    onSuccess: (result) => {
      setDraft(result.value);
      setBaseline(result.value);
      setVisible(true);
      setError(null);
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke hente værdien.'),
  });

  const save = useMutation({
    mutationFn: (value: string) =>
      api.put<IntegrationSettingItem>(`/integrations/config/${encodeURIComponent(setting.name)}`, {
        value,
      }),
    onSuccess: async () => {
      /*
       * Feltet lukkes igen efter gemning: draft tilbage til null, så prikkerne kommer
       * tilbage, og øjet skal hente værdien forfra for at vise den. En nøgle der bliver
       * stående i klartekst på skærmen efter man er gået videre, er en nøgle nogen kan
       * læse over skulderen.
       */
      setDraft(null);
      setBaseline(null);
      setVisible(false);
      setError(null);
      setSaved(true);
      // `is_set` afgør om feltet viser prikker eller "Tom", så listen hentes igen.
      await queryClient.invalidateQueries({ queryKey: ['admin', 'integration-config'] });
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke gemme værdien.'),
  });

  const remove = useMutation({
    mutationFn: () =>
      api.del<{ name: string }>(`/integrations/config/${encodeURIComponent(setting.name)}`),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'integration-config'] });
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke slette nøglen.'),
  });

  const toggleVisible = (): void => {
    if (visible) {
      setVisible(false);
      return;
    }
    // Ingen værdi følger med listen, så første klik henter den. Har vi den allerede — lige
    // hentet, eller lige indtastet — er øjet bare en maske.
    if (draft === null) {
      reveal.mutate();
      return;
    }
    setVisible(true);
  };

  const onChange = (value: string): void => {
    setDraft(value);
    setSaved(false);
    // Skjult tilstand tegner et tomt felt, saa det man taster ville vaere usynligt. Skriver
    // man, aabnes feltet derfor - man skriver oven i den gamle vaerdi, ikke ind i den.
    setVisible(true);
  };

  const dirty = draft !== null && draft !== (baseline ?? '');

  return (
    <div className="space-y-1 border-b border-line py-3 last:border-b-0">
      <label htmlFor={setting.name} className="text-xs font-medium">
        <span className="font-mono">{setting.name}</span>
        {setting.label === setting.name ? null : (
          <span className="ml-2 font-normal text-ink-muted">{setting.label}</span>
        )}
      </label>

      <div className="flex items-center gap-2">
        {/*
          Alle rækker er det samme felt, også en driver-nøgle. En dropdown ville se pænere
          ud, men den kan ikke vise prikker: værdien følger ikke med listen, og et tomt
          <select> uden øje kunne hverken vise eller hente den. Formen håndhæves serverside
          i stedet — 'mock'/'http' afvises med en besked, hvis det er noget andet.
        */}
        <div className="relative flex-1">
          <Input
            id={setting.name}
            className="pr-9 font-mono text-xs text-ink-muted"
            /*
             * Skjult tilstand har en *tom* værdi og prikkerne i placeholderen — ikke
             * `type="password"` med værdien i. Browserens egen maskering tegner ét prik pr.
             * tegn og fortæller dermed hvor lang nøglen er; MASK er et fast antal, så
             * længden først kan ses når man åbner øjet.
             */
            value={visible ? (draft ?? '') : ''}
            placeholder={visible ? '' : setting.is_set ? MASK : 'Tom'}
            autoComplete="off"
            spellCheck={false}
            readOnly={readOnly}
            /* Read-only but revealable stays enabled, so the shown key can be selected and copied. */
            disabled={readOnly && !canReveal}
            onChange={(event) => onChange(event.target.value)}
          />
          {canReveal ? (
            <button
              type="button"
              onClick={toggleVisible}
              disabled={reveal.isPending}
              aria-label={visible ? `Skjul ${setting.name}` : `Vis ${setting.name}`}
              aria-pressed={visible}
              className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center text-ink-muted hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand disabled:opacity-50"
            >
              {visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </button>
          ) : null}
        </div>

        {!readOnly ? (
          <>
            <Button
              size="sm"
              variant="secondary"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate(draft ?? '')}
            >
              Gem
            </Button>

            <Button
              size="sm"
              variant="ghost"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
              aria-label={`Slet ${setting.name}`}
            >
              Slet
            </Button>
          </>
        ) : null}
      </div>

      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {saved && !dirty ? <p className="text-xs text-emerald-700">Gemt</p> : null}
      {setting.hint && !error ? <p className="text-xs text-ink-muted">{setting.hint}</p> : null}
    </div>
  );
}
